// Builds the ownership graph shown on a saved Beneficial Ownership ticket's
// Ownership Tree tab.
//
// Nothing here is fetched. The BO workflow already persists its whole
// `workflow_state` onto the ticket, and the graph agent's output lives inside
// it at `pipeline.<layer>_results.entities[]`, where every entity carries a
// `connected_entities[]` adjacency list. This module turns those layers into a
// single node/edge graph the tree component can walk one hop at a time.
//
// Two sources, in order of preference:
//   1. `workflow_state.pipeline.{ownership,second_layer,third_layer}_results`
//      — the real graph, with country/type/findings per node.
//   2. `beneficialOwnership.second_layer_chains[]` — a pre-flattened
//      subject → via → discovered list kept as a fallback for tickets saved
//      without the pipeline. Same shape, no per-node metadata.
// If neither is present the graph reports `isEmpty`, and the caller hides the
// tab entirely rather than showing an empty diagram.

export type OwnershipBand =
  | 'owners'
  | 'investors'
  | 'holdings'
  | 'people'
  | 'other';

export interface IOwnershipNode {
  name: string;
  country: string;
  type: string;
  kind: 'company' | 'person';
  flagged: boolean;
  findings: string;
  sourceUrl: string;
  sourceDate: string;
}

// One relationship between two entities. A person who is both owner and
// director of the same company is ONE edge carrying two roles, not two edges --
// two edges put the same person on two cards under two bands.
export interface IOwnershipRole {
  relation: string;
  band: OwnershipBand;
  notes: string;
}

export interface IOwnershipEdge {
  to: string;
  // Every role joined for display ("beneficial owner · director").
  relation: string;
  // The most significant role's band -- where the card is filed.
  band: OwnershipBand;
  notes: string;
  sourceUrl: string;
  sourceDate: string;
  // Absent on hand-built graphs (tests); read through edgeRoles().
  roles?: IOwnershipRole[];
}

export interface IOwnershipGraph {
  subject: string;
  nodes: { [key: string]: IOwnershipNode };
  edges: { [key: string]: IOwnershipEdge[] };
  source: 'pipeline' | 'chains' | 'none';
  // Set when the typed name matched companies in several countries and no
  // jurisdiction was picked: one node per country's company, in research
  // order. The subject then has no edges of its own -- it is only the search
  // term the branches hang from. Absent/empty for the single-company tree.
  branches?: string[];
  // Set when several same-name companies couldn't be split by country and the
  // tree was built for one of them: how many others the report covers.
  otherSameName?: number;
  isEmpty: boolean;
  nodeCount: number;
  edgeCount: number;
}

// The graph agent is instructed to copy these relation terms verbatim, so they
// can be matched exactly rather than guessed at. Anything unrecognised falls
// through to 'other', which is why the band is a hint for grouping and never a
// claim about the relationship itself.
const OWNER_RELATIONS = [
  'ultimate parent',
  'parent',
  'shareholder',
  'beneficial owner',
  'ultimate beneficial owner',
  'founder',
  'co-founder',
  'acquired by',
];

// Investors are surfaced in their own band rather than as owners: a financing
// stake is not, on its own, a claim of ownership or control, and grouping VCs
// under "Owners & controllers" overstated their role. Kept a small, explicit
// list (matched exactly, like the other bands) so unrecognised terms still fall
// through to 'other'.
const INVESTOR_RELATIONS = [
  'investor',
  'angel investor',
  'seed investor',
  'venture capital',
  'venture capital investor',
];

const HOLDING_RELATIONS = ['subsidiary', 'affiliate', 'acquired', 'merged with'];

const PEOPLE_RELATIONS = ['director', 'officer'];

// A person whose role has ended is not part of the current control picture, so
// the tree hides them behind a toggle rather than showing them as if they still
// held the post. There is no structured status field in the saved data, so this
// reads the same prose the drawer shows ("Former CFO/Treasurer, resigned
// effective …") — a heuristic, deliberately scoped to PEOPLE relations so it can
// never mistake a company's "formerly known as" alias for a resigned officer, or
// hide a divested former owner (a signal the sanctions work wants kept).
const FORMER_ROLE_HINTS = [
  'former',
  'resigned',
  'stepped down',
  'no longer',
  'retired',
  'outgoing',
  'departed',
  'past director',
  'past officer',
];

export const isFormerRole = (
  band: OwnershipBand,
  ...texts: Array<string | undefined>
): boolean => {
  if (band !== 'people') {
    return false;
  }
  const haystack = texts
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  if (!haystack) {
    return false;
  }
  return FORMER_ROLE_HINTS.some((hint) => haystack.indexOf(hint) !== -1);
};

// Filing order for a multi-role edge: control outranks office, so an owner who
// is also a director is filed with the owners.
const BAND_PRIORITY: OwnershipBand[] = [
  'owners',
  'investors',
  'holdings',
  'people',
  'other',
];

export const edgeRoles = (edge: IOwnershipEdge): IOwnershipRole[] =>
  edge.roles && edge.roles.length
    ? edge.roles
    : [{ relation: edge.relation, band: edge.band, notes: edge.notes }];

export const edgeBands = (edge: IOwnershipEdge): OwnershipBand[] =>
  edgeRoles(edge).map((role) => role.band);

// A multi-role edge is former only when EVERY role is: a current owner who is
// also a former director is still part of the control picture. `findings` is
// node-level prose that can't be attributed to one role, so it only feeds the
// single-role case, exactly as before roles were merged.
export const isFormerEdge = (
  edge: IOwnershipEdge,
  findings?: string,
): boolean => {
  const roles = edgeRoles(edge);
  if (roles.length === 1) {
    return isFormerRole(roles[0].band, roles[0].relation, roles[0].notes, findings);
  }
  return roles.every((role) => isFormerRole(role.band, role.relation, role.notes));
};

export const relationBand = (relation: any): OwnershipBand => {
  const text = String(relation || '').trim().toLowerCase();
  if (OWNER_RELATIONS.indexOf(text) !== -1) {
    return 'owners';
  }
  if (INVESTOR_RELATIONS.indexOf(text) !== -1) {
    return 'investors';
  }
  if (HOLDING_RELATIONS.indexOf(text) !== -1) {
    return 'holdings';
  }
  if (PEOPLE_RELATIONS.indexOf(text) !== -1) {
    return 'people';
  }
  return 'other';
};

// Deliberately conservative. Case, surrounding punctuation and repeated
// whitespace are noise; legal suffixes are NOT stripped, because "Meridian
// Ltd" and "Meridian Group Ltd" are routinely different companies in these
// structures and collapsing them would invent a relationship.
export const normalizeEntityName = (value: any): string =>
  String(value === null || value === undefined ? '' : value)
    .replace(/[‘’]/g, "'")
    .replace(/\s+/g, ' ')
    .replace(/^[\s.,;:'"()[\]-]+|[\s.,;:'"()[\]-]+$/g, '')
    .trim()
    .toLowerCase();

const PERSON_TYPE_HINTS = [
  'chairman',
  'chairperson',
  'chief',
  'ceo',
  'cfo',
  'coo',
  'cto',
  'director',
  'officer',
  'secretary',
  'president',
  'founder',
  'individual',
  'person',
  'shareholder (individual)',
  'manager',
  'partner (individual)',
];

// Only a guess, and only drives which glyph the card shows. The workflow does
// not label people explicitly, so a wrong icon is the worst case here.
const guessKind = (type: any, relation: any): 'company' | 'person' => {
  const text = String(type || '').trim().toLowerCase();
  if (text) {
    for (let i = 0; i < PERSON_TYPE_HINTS.length; i += 1) {
      if (text.indexOf(PERSON_TYPE_HINTS[i]) !== -1) {
        return 'person';
      }
    }
    return 'company';
  }
  const rel = String(relation || '').trim().toLowerCase();
  return PEOPLE_RELATIONS.indexOf(rel) !== -1 ? 'person' : 'company';
};

const asArray = (value: any): any[] => (Array.isArray(value) ? value : []);

const text = (value: any): string =>
  typeof value === 'string' && value.trim() ? value.trim() : '';

// Same "is this flag saying anything" test the ticket summary uses.
const EMPTY_FLAGS = [
  '',
  'none',
  'no',
  'not confirmed',
  'not available',
  'n/a',
  'na',
  'null',
  'unknown',
];

const isFlagValue = (value: any): boolean => {
  const flag = String(value || '').trim().toLowerCase();
  return flag !== '' && EMPTY_FLAGS.indexOf(flag) === -1;
};

const getWorkflowData = (ticket: any): any => {
  const workflowData = Array.isArray(ticket?.workflowData)
    ? ticket.workflowData[0]
    : ticket?.workflowData;
  return workflowData || {};
};

const getResult = (ticket: any): any => {
  const workflowData = getWorkflowData(ticket);
  return ticket?.beneficialOwnership || workflowData?.result || {};
};

interface IBuilder {
  nodes: { [key: string]: IOwnershipNode };
  edges: { [key: string]: IOwnershipEdge[] };
}

// Every map below is keyed by a normalized ENTITY NAME, which comes from the
// screening agent and the indexes -- not from us. A plain `{}` inherits
// Object.prototype, so an entity called "__proto__" reassigns the map's
// prototype instead of adding an entry, and one called "constructor" or
// "toString" reads back as an inherited function rather than undefined,
// which renders as a broken node. Null-prototype objects have no inherited
// keys to collide with; Object.keys / for-in / spread all still work.
const emptyDictionary = <T>(): { [key: string]: T } =>
  Object.create(null) as { [key: string]: T };

const emptyBuilder = (): IBuilder => ({
  nodes: emptyDictionary<IOwnershipNode>(),
  edges: emptyDictionary<IOwnershipEdge[]>(),
});

// First writer of a node wins on identity; later mentions only fill in blanks.
// The layer that had an entity as its own research target carries the richest
// record (country, type, findings), and a passing mention in someone else's
// connected_entities[] must never overwrite it with empty strings.
const upsertNode = (
  builder: IBuilder,
  name: string,
  patch: Partial<IOwnershipNode>,
): void => {
  upsertKey(builder, normalizeEntityName(name), name, patch);
};

// upsertNode with the key chosen by the caller -- a jurisdiction branch is keyed
// on name + country, so two same-name companies stay two nodes.
const upsertKey = (
  builder: IBuilder,
  key: string,
  name: string,
  patch: Partial<IOwnershipNode>,
): void => {
  if (!key) {
    return;
  }

  const existing = builder.nodes[key];
  if (!existing) {
    builder.nodes[key] = {
      name: text(name),
      country: patch.country || '',
      type: patch.type || '',
      kind: patch.kind || 'company',
      flagged: Boolean(patch.flagged),
      findings: patch.findings || '',
      sourceUrl: patch.sourceUrl || '',
      sourceDate: patch.sourceDate || '',
    };
    return;
  }

  existing.country = existing.country || patch.country || '';
  existing.type = existing.type || patch.type || '';
  existing.findings = existing.findings || patch.findings || '';
  existing.sourceUrl = existing.sourceUrl || patch.sourceUrl || '';
  existing.sourceDate = existing.sourceDate || patch.sourceDate || '';
  existing.flagged = existing.flagged || Boolean(patch.flagged);
  if (patch.kind === 'person') {
    existing.kind = 'person';
  }
};

// Recomputes an edge's display fields from its roles. The 'connected'
// placeholder (a mention with no relation) is dropped once a real role exists.
const applyRoles = (edge: IOwnershipEdge, roles: IOwnershipRole[]): void => {
  const real = roles.filter((role) => role.relation.toLowerCase() !== 'connected');
  const kept = real.length ? real : roles.slice(0, 1);
  const notes: string[] = [];
  kept.forEach((role) => {
    if (role.notes && notes.indexOf(role.notes) === -1) {
      notes.push(role.notes);
    }
  });

  edge.roles = kept;
  edge.relation = kept.map((role) => role.relation).join(' · ');
  edge.band = BAND_PRIORITY.filter(
    (band) => kept.some((role) => role.band === band),
  )[0] || 'other';
  edge.notes = notes.join(' ');
};

// Adds roles to the single edge between two keys, creating it if needed.
// Keyed on the entity pair, not pair + relation: the same person connected twice
// as "beneficial owner" and "director" is one card with both roles.
const linkKeys = (
  builder: IBuilder,
  fromKey: string,
  toKey: string,
  roles: IOwnershipRole[],
  extra: { sourceUrl?: string; sourceDate?: string },
): void => {
  // Self-loops carry no information and would render as a card pointing at the
  // subject it sits under.
  if (!fromKey || !toKey || fromKey === toKey) {
    return;
  }

  const list = builder.edges[fromKey] || (builder.edges[fromKey] = []);
  let edge = list.filter((item) => item.to === toKey)[0];
  if (!edge) {
    edge = {
      to: toKey,
      relation: '',
      band: 'other',
      notes: '',
      sourceUrl: extra.sourceUrl || '',
      sourceDate: extra.sourceDate || '',
      roles: [],
    };
    list.push(edge);
  } else {
    edge.sourceUrl = edge.sourceUrl || extra.sourceUrl || '';
    edge.sourceDate = edge.sourceDate || extra.sourceDate || '';
  }

  const merged = (edge.roles || []).map((role) => ({ ...role }));
  roles.forEach((role) => {
    const same = merged.filter(
      (item) => item.relation.toLowerCase() === role.relation.toLowerCase(),
    )[0];
    if (!same) {
      merged.push({ ...role });
    } else if (!same.notes) {
      same.notes = role.notes;
    }
  });
  applyRoles(edge, merged);
};

const addEdge = (
  builder: IBuilder,
  from: string,
  to: string,
  relation: string,
  extra: { notes?: string; sourceUrl?: string; sourceDate?: string },
): void => {
  linkKeys(
    builder,
    normalizeEntityName(from),
    normalizeEntityName(to),
    [
      {
        relation: text(relation) || 'connected',
        band: relationBand(relation),
        notes: extra.notes || '',
      },
    ],
    extra,
  );
};

// Names the ticket's own result arrays report a sanctions flag for. Used to
// mark nodes the graph agent itself says nothing about — the graph and the
// sanctions screen are separate agents, so the flag lives on the result.
const collectFlaggedNames = (result: any): { [key: string]: boolean } => {
  const flagged = emptyDictionary<boolean>();

  const scan = (rows: any[], nameKey: string, flagKey: string) => {
    rows.forEach((row: any) => {
      const key = normalizeEntityName(row?.[nameKey]);
      if (key && isFlagValue(row?.[flagKey])) {
        flagged[key] = true;
      }
    });
  };

  scan(asArray(result?.owners), 'name', 'sanctions_flag');
  scan(asArray(result?.associated_entities), 'name', 'sanctions_flag');

  asArray(result?.sanctions_connections).forEach((row: any) => {
    const key = normalizeEntityName(row?.sanctioned_entity);
    if (key) {
      flagged[key] = true;
    }
  });

  return flagged;
};

// `keyFor` lets the caller key an entity's own record somewhere other than its
// name (a jurisdiction branch); its connections are always keyed by name.
const readLayer = (
  builder: IBuilder,
  layer: any,
  keyFor?: (entity: any) => string,
): void => {
  const entities = asArray(layer?.entities);

  entities.forEach((entity: any) => {
    const name = text(entity?.name);
    if (!name) {
      return;
    }

    const sourceKey = (keyFor && keyFor(entity)) || normalizeEntityName(name);
    upsertKey(builder, sourceKey, name, {
      country: text(entity?.country),
      type: text(entity?.type),
      kind: guessKind(entity?.type, ''),
      findings: text(entity?.findings),
    });
    const source = builder.nodes[sourceKey];
    const sourceIsPerson = Boolean(source && source.kind === 'person');

    asArray(entity?.connected_entities).forEach((connection: any) => {
      const target = text(connection?.name);
      if (!target) {
        return;
      }
      const relation = text(connection?.relation);
      // "director"/"officer" names the PERSON's role. Under a company that is
      // the target; under a person (a second-layer officer's own ventures) it
      // is the person's post AT the target, so the target is an organisation.
      const targetKind =
        sourceIsPerson && relationBand(relation) === 'people'
          ? 'company'
          : guessKind('', relation);
      upsertNode(builder, target, {
        kind: targetKind,
        sourceUrl: text(connection?.source_url),
        sourceDate: text(connection?.source_date),
      });
      linkKeys(
        builder,
        sourceKey,
        normalizeEntityName(target),
        [
          {
            relation: relation || 'connected',
            band: relationBand(relation),
            notes: text(connection?.notes),
          },
        ],
        {
          sourceUrl: text(connection?.source_url),
          sourceDate: text(connection?.source_date),
        },
      );
    });
  });
};

// The subject is keyed on the name typed at intake, but the research agents key
// their own record of it on the name they FOUND ("Maxtech" vs "Maxtech
// Corporation Ltd."). normalizeEntityName deliberately won't equate those, so
// the whole first hop hangs off a node the tree never reaches, and the tab/PDF
// render the subject card with nothing under it.
//
// The first-layer prompt has entities[] start with the primary subject, and
// later entries can be same-name look-alikes from other jurisdictions -- so
// only entities[0] is treated as the subject, and only when the subject has no
// pipeline edges of its own. Its edges and metadata move onto the subject; the
// alias node is dropped. Returns the alias key (or '') so later steps can treat
// rows naming it as the subject too.
//
// The alias must also carry every word of the typed name (legal forms aside).
// Research naming the fuller registered name is expected ("Maxtech" ->
// "Maxtech Data House Private Limited"); research naming something with a
// typed word MISSING is not -- "Alpha Shipping" in entities[0] for a search on
// "Alpha Shipping Shanghai" is a different company, and merging it would file
// that company's owners under the subject.
const LEGAL_FORM_TOKENS = [
  'co',
  'company',
  'corp',
  'corporation',
  'inc',
  'incorporated',
  'ltd',
  'limited',
  'llc',
  'llp',
  'plc',
  'gmbh',
  'ag',
  'sa',
  'spa',
  'nv',
  'bv',
  'pty',
  'sarl',
  'jsc',
  'pjsc',
  'ojsc',
  'ooo',
  'oao',
  'zao',
  'pao',
  'ao',
];

const nameWords = (name: string): string[] =>
  normalizeEntityName(name)
    .replace(/[.,;:'"()[\]/&-]+/g, ' ')
    .split(' ')
    .filter((token) => token && LEGAL_FORM_TOKENS.indexOf(token) === -1);

const containsTypedName = (typedName: string, foundName: string): boolean => {
  const typed = nameWords(typedName);
  const found = nameWords(foundName);
  return (
    typed.length > 0 && typed.every((word) => found.indexOf(word) !== -1)
  );
};

const mergeSubjectAlias = (
  builder: IBuilder,
  subjectName: string,
  ownershipResults: any,
): string => {
  const subjectKey = normalizeEntityName(subjectName);
  if (!subjectKey || (builder.edges[subjectKey] || []).length) {
    return '';
  }

  const first = asArray(ownershipResults?.entities)[0];
  const aliasKey = normalizeEntityName(text(first?.name));
  const aliasEdges = aliasKey ? builder.edges[aliasKey] || [] : [];
  if (
    !aliasKey ||
    aliasKey === subjectKey ||
    !aliasEdges.length ||
    !containsTypedName(subjectName, text(first?.name))
  ) {
    return '';
  }

  const alias = builder.nodes[aliasKey];
  upsertNode(builder, subjectName, {
    country: alias?.country,
    type: alias?.type,
    findings: alias?.findings,
    sourceUrl: alias?.sourceUrl,
    sourceDate: alias?.sourceDate,
    flagged: alias?.flagged,
  });

  aliasEdges.forEach((edge) => {
    linkKeys(builder, subjectKey, edge.to, edgeRoles(edge), edge);
  });
  delete builder.edges[aliasKey];
  delete builder.nodes[aliasKey];

  // Anything else that pointed at the alias now points at the subject, merged
  // into any edge it already had there. linkKeys drops the self-loop a redirect
  // can leave.
  Object.keys(builder.edges).forEach((from) => {
    const list = builder.edges[from];
    const pointing = list.filter((edge) => edge.to === aliasKey);
    if (!pointing.length) {
      return;
    }
    builder.edges[from] = list.filter((edge) => edge.to !== aliasKey);
    pointing.forEach((edge) => {
      linkKeys(builder, from, subjectKey, edgeRoles(edge), edge);
    });
  });

  return aliasKey;
};

// ---- Jurisdiction branches ------------------------------------------------
// With no jurisdiction picked, research covers every company carrying the typed
// name -- Maxtech in India, Russia and Ukraine, each with its own owners. Keyed
// by name alone they collapse into one node and their structures mix (a Russian
// company's sanctioned owners filed under the Indian one). So when the typed
// name resolves to companies in two or more countries, each becomes its own
// branch, keyed by name + country, and the subject node is only the search
// term above them. A picked jurisdiction always keeps the single-company tree.
const NO_JURISDICTION = [
  '',
  'none',
  'none specified',
  'not specified',
  'unspecified',
  'unknown',
  'any',
  'all',
  'all jurisdictions',
  'any jurisdiction',
  'global',
  'worldwide',
  'n/a',
  'na',
];

const BRANCH_SEPARATOR = '@@';
// Suffix for a plain "Maxtech" that can't be placed in one branch.
const UNPLACED_SUFFIX = `${BRANCH_SEPARATOR}?`;

const branchKeyFor = (entity: any): string =>
  `${normalizeEntityName(
    text(entity?.name),
  )}${BRANCH_SEPARATOR}${normalizeEntityName(text(entity?.country))}`;

// The first-layer entities carrying every typed word: the subject's own record
// plus any same-name companies research covered.
const findVariants = (subjectName: string, ownershipResults: any): any[] =>
  subjectName
    ? asArray(ownershipResults?.entities).filter(
        (entity: any) =>
          text(entity?.name) &&
          containsTypedName(subjectName, text(entity?.name)),
      )
    : [];

const distinctVariantCount = (variants: any[]): number => {
  const keys = emptyDictionary<boolean>();
  variants.forEach((entity: any) => {
    keys[branchKeyFor(entity)] = true;
  });
  return Object.keys(keys).length;
};

const jurisdictionPicked = (pickedCountry: any): boolean =>
  NO_JURISDICTION.indexOf(normalizeEntityName(pickedCountry)) === -1;

// Variants that become country branches, or [] for a single-company tree.
const findBranchEntities = (variants: any[], pickedCountry: any): any[] => {
  if (jurisdictionPicked(pickedCountry)) {
    return [];
  }
  const countries = emptyDictionary<boolean>();
  variants.forEach((entity: any) => {
    const country = normalizeEntityName(text(entity?.country));
    if (country) {
      countries[country] = true;
    }
  });
  const hasConnections = variants.some(
    (entity: any) => asArray(entity?.connected_entities).length > 0,
  );
  return Object.keys(countries).length >= 2 && hasConnections ? variants : [];
};

// ---- One company picked from several same-name matches --------------------
// When the same-name companies can't be split by country (a picked
// jurisdiction, blank countries, two in one country), the tree is built for ONE
// of them and the rest stay in the report; the subject card says how many. In
// order: the company the report settled on, the one in the picked
// jurisdiction, an exact name match, then whichever research connected most.
// Ties keep research order.
const pickSubjectVariant = (
  variants: any[],
  subjectName: string,
  result: any,
  pickedCountry: any,
): any => {
  const reportName = normalizeEntityName(text(result?.normalized_subject_name));
  const picked = jurisdictionPicked(pickedCountry)
    ? normalizeEntityName(pickedCountry)
    : '';
  const typed = nameWords(subjectName).sort().join(' ');
  const score = (entity: any): number[] => {
    const country = normalizeEntityName(text(entity?.country));
    return [
      reportName && normalizeEntityName(text(entity?.name)) === reportName
        ? 1
        : 0,
      picked &&
      country &&
      (country.indexOf(picked) !== -1 || picked.indexOf(country) !== -1)
        ? 1
        : 0,
      nameWords(text(entity?.name)).sort().join(' ') === typed ? 1 : 0,
      asArray(entity?.connected_entities).length,
    ];
  };
  let best = variants[0];
  let bestScore = score(best);
  variants.slice(1).forEach((entity: any) => {
    const next = score(entity);
    for (let i = 0; i < next.length; i += 1) {
      if (next[i] !== bestScore[i]) {
        if (next[i] > bestScore[i]) {
          best = entity;
          bestScore = next;
        }
        return;
      }
    }
  });
  return best;
};

// Reads the layers with each same-name company on its own key (`keyOf`), then
// re-homes every link that names the subject (or one of the companies) without
// saying which. Returns the keys whose structures are drawn: every country
// branch, or the subject plus the companies left in the report when one was
// picked.
const separateVariants = (
  builder: IBuilder,
  subjectName: string,
  pipeline: any,
  variants: any[],
  keyOf: (entity: any) => string,
): string[] => {
  // Plain names that mean "one of the Maxtechs" without saying which.
  const ambiguous = emptyDictionary<string>();
  ambiguous[normalizeEntityName(subjectName)] = subjectName;
  variants.forEach((entity: any) => {
    ambiguous[normalizeEntityName(text(entity?.name))] = text(entity?.name);
  });

  readLayer(builder, pipeline?.ownership_results, (entity: any) =>
    variants.indexOf(entity) !== -1 ? keyOf(entity) : '',
  );
  // A later layer researching a plain "Maxtech" again can't say which one it
  // is: its record goes on its own unplaced node.
  const parkPlain = (entity: any): string => {
    const key = normalizeEntityName(text(entity?.name));
    return ambiguous[key] !== undefined ? `${key}${UNPLACED_SUFFIX}` : '';
  };
  readLayer(builder, pipeline?.second_layer_results, parkPlain);
  readLayer(builder, pipeline?.third_layer_results, parkPlain);

  const roots: string[] = [];
  variants.forEach((entity: any) => {
    const key = keyOf(entity);
    if (roots.indexOf(key) === -1) {
      roots.push(key);
    }
  });

  // The company (or companies) each entity was found under, walking down from
  // every root without stepping through an ambiguous name (that would leak one
  // company's structure into another's).
  const homes = emptyDictionary<string[]>();
  roots.forEach((root) => {
    const seen = emptyDictionary<boolean>();
    const queue = [root];
    seen[root] = true;
    while (queue.length) {
      const from = queue.shift() as string;
      const list = homes[from] || (homes[from] = []);
      if (list.indexOf(root) === -1) {
        list.push(root);
      }
      (builder.edges[from] || []).forEach((edge) => {
        if (!seen[edge.to] && ambiguous[edge.to] === undefined) {
          seen[edge.to] = true;
          queue.push(edge.to);
        }
      });
    }
  });

  // A plain-name stub left by a passing mention is not a company of its own.
  Object.keys(ambiguous).forEach((key) => {
    if (roots.indexOf(key) === -1) {
      delete builder.nodes[key];
    }
  });

  // Found under exactly one company: the link means that company. Otherwise it
  // points at the unplaced node rather than a guess. linkKeys drops the
  // self-loop a company naming itself leaves.
  Object.keys(builder.edges).forEach((from) => {
    const list = builder.edges[from];
    const pointing = list.filter((edge) => ambiguous[edge.to] !== undefined);
    if (!pointing.length) {
      return;
    }
    builder.edges[from] = list.filter(
      (edge) => ambiguous[edge.to] === undefined,
    );
    const home = homes[from] || [];
    pointing.forEach((edge) => {
      const to = home.length === 1 ? home[0] : `${edge.to}${UNPLACED_SUFFIX}`;
      if (home.length !== 1 && !builder.nodes[to]) {
        upsertKey(builder, to, ambiguous[edge.to], {});
      }
      linkKeys(builder, from, to, edgeRoles(edge), edge);
    });
  });

  return roots;
};

// How many connections the subject card / "View relationship tree" badge
// reports: the subject's own, or every branch's when the tree is branched.
export const subjectConnectionCount = (graph: IOwnershipGraph): number => {
  const branches = graph.branches || [];
  if (branches.length) {
    return branches.reduce(
      (total, branch) => total + (graph.edges[branch] || []).length,
      0,
    );
  }
  return (graph.edges[graph.subject] || []).length;
};

// The line under the subject card when the tree was built for one of several
// same-name companies, e.g. "2 other companies named Maxtech appear in the
// report." Empty otherwise.
export const sameNameNote = (graph: IOwnershipGraph): string => {
  const others = graph.otherSameName || 0;
  const subject = graph.nodes[graph.subject];
  if (!others || !subject) {
    return '';
  }
  return `${others} other ${others === 1 ? 'company' : 'companies'} named ${
    subject.name
  } ${others === 1 ? 'appears' : 'appear'} in the report.`;
};

export const buildOwnershipGraph = (ticket: any): IOwnershipGraph => {
  const workflowData = getWorkflowData(ticket);
  const result = getResult(ticket);
  const state = workflowData?.workflow_state || {};
  const pipeline = state?.pipeline || {};

  const subjectName =
    text(workflowData?.entity_name) ||
    text(result?.normalized_subject_name) ||
    text(result?.query_name) ||
    text(ticket?.classificationValue) ||
    text(ticket?.commodity?.commodityName) ||
    '';

  const builder = emptyBuilder();
  let source: IOwnershipGraph['source'] = 'none';

  const subjectKey = normalizeEntityName(subjectName);
  const variants = findVariants(subjectName, pipeline?.ownership_results);
  const branchEntities = findBranchEntities(variants, workflowData?.country);
  let branches: string[] = [];
  let subjectAliasKey = '';
  let otherSameName = 0;
  // Each same-name company's own key, by name: which node a sanctions flag
  // naming one of them belongs to.
  const variantKeys = emptyDictionary<string[]>();
  const noteVariant = (entity: any, key: string) => {
    const nameKey = normalizeEntityName(text(entity?.name));
    const list = variantKeys[nameKey] || (variantKeys[nameKey] = []);
    if (list.indexOf(key) === -1) {
      list.push(key);
    }
  };
  if (branchEntities.length) {
    branches = separateVariants(
      builder,
      subjectName,
      pipeline,
      branchEntities,
      branchKeyFor,
    );
    branchEntities.forEach((entity: any) =>
      noteVariant(entity, branchKeyFor(entity)),
    );
  } else if (distinctVariantCount(variants) >= 2) {
    const chosen = pickSubjectVariant(
      variants,
      subjectName,
      result,
      workflowData?.country,
    );
    const keyOf = (entity: any): string =>
      entity === chosen ? subjectKey : branchKeyFor(entity);
    separateVariants(builder, subjectName, pipeline, variants, keyOf);
    variants.forEach((entity: any) => noteVariant(entity, keyOf(entity)));
    // The card keeps the name that was searched; its country and type say
    // which company it is.
    if (builder.nodes[subjectKey]) {
      builder.nodes[subjectKey].name = subjectName;
    }
    otherSameName = distinctVariantCount(variants) - 1;
  } else {
    readLayer(builder, pipeline?.ownership_results);
    readLayer(builder, pipeline?.second_layer_results);
    readLayer(builder, pipeline?.third_layer_results);

    subjectAliasKey = mergeSubjectAlias(
      builder,
      subjectName,
      pipeline?.ownership_results,
    );
  }

  // Count the edges the builder actually KEPT, not the ones we attempted.
  // addEdge silently drops self-loops and duplicates, so a layer whose every
  // connection is one of those used to still report "the pipeline worked" --
  // suppressing the second_layer_chains fallback and leaving an empty tree
  // when usable fallback data existed.
  const fromPipeline = Object.keys(builder.edges).reduce(
    (total, key) => total + builder.edges[key].length,
    0,
  );

  if (fromPipeline > 0) {
    source = 'pipeline';
  } else {
    branches = [];
    // Fallback: the flattened chains keep subject → via → discovered, which is
    // still two real levels, just without country/type/findings per node.
    const chains = asArray(result?.second_layer_chains);
    chains.forEach((chain: any) => {
      const via = text(chain?.via_entity);
      const discovered = text(chain?.discovered_entity);
      if (!via || !discovered) {
        return;
      }
      const relation = text(chain?.relation);
      upsertNode(builder, via, {});
      upsertNode(builder, discovered, { kind: guessKind('', relation) });
      addEdge(builder, via, discovered, relation, {});
      if (subjectName) {
        addEdge(builder, subjectName, via, 'connected', {});
      }
    });
    if (Object.keys(builder.edges).length) {
      source = 'chains';
    }
  }

  // The subject's own direct relationships are reported by the analysis arrays
  // as well, and on a first-layer-only run they can be the only thing that
  // gives the subject any children at all.
  if (subjectName) {
    upsertNode(builder, subjectName, {
      country: text(workflowData?.country),
      findings: text(result?.executive_summary) || text(result?.general_description),
    });

    // Every entity the pipeline already placed under a parent. owners[],
    // ownership_findings[] and associated_entities[] are FLAT lists written
    // relative to the subject — they include second- and third-layer
    // discoveries with no record of which entity they were found under. Wiring
    // all of them to the subject is what flattened the hierarchy: an officer
    // discovered under an acquired subsidiary appeared as an officer of the
    // subject itself. So the pipeline's parenting wins, and these rows only
    // supply metadata plus entities the graph never placed at all.
    const hasParent = emptyDictionary<boolean>();
    Object.keys(builder.edges).forEach((from) => {
      builder.edges[from].forEach((edge) => {
        hasParent[edge.to] = true;
      });
    });

    const directRows = asArray(result?.owners)
      .concat(asArray(result?.ownership_findings))
      .concat(asArray(result?.associated_entities));

    directRows.forEach((row: any) => {
      const name = text(row?.name);
      // A row naming the subject's merged alias is the subject itself; letting
      // it through would recreate the alias as a child of the subject.
      if (!name || normalizeEntityName(name) === subjectAliasKey) {
        return;
      }
      // A row naming one of the same-name companies is a company, not one of
      // the subject's connections; its flag is applied below.
      if (variantKeys[normalizeEntityName(name)]) {
        return;
      }
      // Branched: these lists are relative to "the subject" and don't say which
      // country's company, so they only fill in metadata for entities the
      // branches already placed. They stay listed in Ownership Found.
      if (
        branches.length &&
        (!builder.nodes[normalizeEntityName(name)] ||
          normalizeEntityName(name) === normalizeEntityName(subjectName))
      ) {
        return;
      }
      const relation =
        text(row?.relationship_to_subject) ||
        text(row?.relation) ||
        text(row?.relationship);

      // Metadata is merged either way — upsertNode never overwrites a richer
      // record, so this only fills gaps the graph layers left.
      upsertNode(builder, name, {
        kind: guessKind('', relation),
        findings: text(row?.finding),
        sourceUrl: text(row?.source_url),
        sourceDate: text(row?.source_date),
        flagged: isFlagValue(row?.sanctions_flag),
      });

      if (branches.length || hasParent[normalizeEntityName(name)]) {
        return;
      }

      addEdge(builder, subjectName, name, relation, {
        notes: text(row?.notes),
        sourceUrl: text(row?.source_url),
        sourceDate: text(row?.source_date),
      });
    });

    if (source === 'none' && Object.keys(builder.edges).length) {
      source = 'chains';
    }
  }

  const flagged = collectFlaggedNames(result);
  const separated = branches.length > 0 || otherSameName > 0;
  Object.keys(flagged).forEach((key) => {
    if (separated) {
      // A flagged name shared by two same-name companies can't say which one
      // is listed, and the typed name alone doesn't either: flag neither.
      const named = variantKeys[key] || [];
      if (named.length === 1 && builder.nodes[named[0]]) {
        builder.nodes[named[0]].flagged = true;
      }
      if (named.length || key === subjectKey) {
        return;
      }
    }
    const nodeKey =
      subjectAliasKey && key === subjectAliasKey ? subjectKey : key;
    if (builder.nodes[nodeKey]) {
      builder.nodes[nodeKey].flagged = true;
    }
  });

  const edgeCount = Object.keys(builder.edges).reduce(
    (total, key) => total + builder.edges[key].length,
    0,
  );

  return {
    subject: subjectKey,
    nodes: builder.nodes,
    edges: builder.edges,
    source: edgeCount ? source : 'none',
    branches: branches,
    otherSameName: otherSameName,
    isEmpty: !edgeCount || !subjectKey || !builder.nodes[subjectKey],
    nodeCount: Object.keys(builder.nodes).length,
    edgeCount: edgeCount,
  };
};

export const hasOwnershipGraph = (ticket: any): boolean =>
  !buildOwnershipGraph(ticket).isEmpty;
